import axios from 'axios';
import { MutationTree, ActionTree, GetterTree } from 'vuex';
import { RootState } from './index.d';
import {
  AuthState,
  User,
  UserData,
  AuthCredentials,
  RegisterCredentials,
  PasswordResetCredentials,
} from './authStore.d';
import config from '../config';

const { baseApiDomain, apiRoutes } = config;

const authState: AuthState = {
  userData: null,
  resetPasswordLoadingState: 'idle',
  registerLoadingState: 'idle',
};

const mutations: MutationTree<AuthState> = {
  setUserData(state, userData: UserData) {
    state.userData = userData;
    localStorage.setItem('user', JSON.stringify(userData));

    axios.defaults.headers.common.Authorization = `Bearer ${userData.token}`;
  },

  setUser(state, user: User) {
    if (!state.userData) {
      return;
    }

    state.userData.user = user;
  },

  clearUserData() {
    localStorage.removeItem('user');
    window.location.reload();
  },

  setResetPasswordLoadingState(state, loadingState) {
    state.resetPasswordLoadingState = loadingState;
  },

  setRegisterLoadingState(state, loadingState) {
    state.registerLoadingState = loadingState;
  },
};

const actions: ActionTree<AuthState, RootState> = {
  async login({ commit, dispatch }, credentials: AuthCredentials) {
    await dispatch('fetchCsrf');

    return axios
      .post(apiRoutes.login, credentials)
      .then(({ data }) => {
        commit('setUserData', data);
        dispatch('fetchUser');
      });
  },

  async loginCheck({ dispatch }, email: string) {
    await dispatch('fetchCsrf');

    return axios
      .post(apiRoutes.loginCheck, {
        email,
      });
  },

  logout({ commit }) {
    commit('clearUserData');
  },

  async register({ dispatch, commit }, credentials: RegisterCredentials) {
    await dispatch('fetchCsrf');
    commit('setRegisterLoadingState', 'loading');

    return axios
      .post(apiRoutes.register, credentials)
      .then((res) => {
        commit('setRegisterLoadingState', 'loaded');
        return res;
      })
      .catch((err) => {
        commit('setRegisterLoadingState', 'error');
        throw err;
      });
  },

  async resetPassword({ dispatch, commit }, credentials: PasswordResetCredentials) {
    await dispatch('fetchCsrf');
    commit('setResetPasswordLoadingState', 'loading');

    return axios
      .post(apiRoutes.passwordReset, credentials)
      .then((res) => {
        commit('setResetPasswordLoadingState', 'loaded');
        return res;
      })
      .catch((err) => {
        commit('setResetPasswordLoadingState', 'error');
        throw err;
      });
  },

  retrieveUserFromStorage({ commit }) {
    const userInfo = localStorage.getItem('user');

    if (userInfo) {
      const userData = JSON.parse(userInfo);
      commit('setUserData', userData);
      return Promise.resolve();
    }

    return Promise.reject();
  },

  fetchCsrf() {
    return axios.get(apiRoutes.sanctumCsrf, { baseURL: baseApiDomain })
      .then(() => {
        //
      });
  },

  fetchUser({ commit }) {
    return axios
      .get(apiRoutes.user)
      .then(({ data }) => {
        commit('setUser', data);
      });
  },
};

const getters: GetterTree<AuthState, RootState> = {
  isLoggedIn(state) {
    return !!state.userData;
  },

  userData(state) {
    return state.userData;
  },

  user(state) {
    return state.userData
      ? state.userData.user
      : null;
  },

  resetPasswordLoadingState(state) {
    return state.resetPasswordLoadingState;
  },

  registerLoadingState(state) {
    return state.registerLoadingState;
  },
};

export default {
  state: authState,
  getters,
  mutations,
  actions,
};
